import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Navbar from "../components/Navbar";
import EngineSchematic from "../components/EngineSchematic";
import UnderTheHood from "../components/UnderTheHood";
import Footer from "../components/Footer";
import StoreButtons from "../components/StoreButtons";
import useBreadcrumbJsonLd from "../lib/useBreadcrumbJsonLd";
import useJsonLd from "../lib/useJsonLd";
import {
    METHODOLOGY_PAPERS,
    METHODOLOGY_AUTHOR,
} from "../data/methodology";
import { BLOG_META } from "../data/blogMeta";

const ENGINE_CRUMBS = [
    { name: "Home", url: "https://trtplus.app/" },
    { name: "The Engine", url: "https://trtplus.app/engine" },
];

// TechArticle schema for /engine. Marks the page as a citable
// technical/scholarly resource rather than a product surface — improves
// AI citation eligibility (ChatGPT, Claude, Perplexity) for queries
// about pharmacokinetics, hormone optimization, biomarker relationships.
// dateModified is computed at render time so it auto-refreshes on every
// deploy; datePublished is the original /engine launch.
const ENGINE_ARTICLE = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    "@id": "https://trtplus.app/engine#article",
    headline:
        "The TRT Plus Engine — Pharmacokinetic Modeling, Biomarker Relationships and Scoring Logic",
    description:
        "Inside the TRT Plus optimization engine — 16 biomarkers, 8 clinical relationships, and the underlying mathematical models powering protocol calibration and bloodwork analysis.",
    url: "https://trtplus.app/engine",
    mainEntityOfPage: "https://trtplus.app/engine",
    image: "https://trtplus.app/og-image.jpg?v=3",
    isPartOf: { "@id": "https://trtplus.app/#website" },
    datePublished: "2025-11-21",
    dateModified: new Date().toISOString().slice(0, 10),
    author: {
        "@type": "Person",
        name: "Nathan Southern",
        affiliation: { "@id": "https://trtplus.app/#organization" },
    },
    publisher: { "@id": "https://trtplus.app/#organization" },
    inLanguage: "en-US",
    about: [
        { "@type": "Thing", name: "Pharmacokinetics" },
        { "@type": "Thing", name: "Testosterone Replacement Therapy" },
        { "@type": "Thing", name: "Hormone Optimization" },
        { "@type": "Thing", name: "Free Testosterone Calculation" },
        { "@type": "Thing", name: "Cardio-Metabolic Risk" },
    ],
    keywords:
        "pharmacokinetics, TRT, testosterone, Vermeulen, Zakharov, Michaelis-Menten, Cardio-Metabolic Cascade, biomarker analysis, A:E ratio, hematocrit, peak-to-trough fluctuation",
    mentions: [
        { "@type": "DefinedTerm", name: "Testosterone" },
        { "@type": "DefinedTerm", name: "Estradiol" },
        { "@type": "DefinedTerm", name: "Dihydrotestosterone" },
    ],
    citation: METHODOLOGY_PAPERS.map((p) => ({
        "@type": "ScholarlyArticle",
        "@id": `https://doi.org/${p.doi}`,
        name: p.title,
        headline: p.title,
        identifier: p.doi,
        sameAs: p.url,
        url: p.url,
    })),
};

// One ScholarlyArticle entry per Zenodo paper. Emitted alongside the
// TechArticle so Google Scholar, Perplexity, and other AI crawlers can
// resolve each DOI as an independent citable work.
const METHODOLOGY_ARTICLE_JSONLD = {
    "@context": "https://schema.org",
    "@graph": METHODOLOGY_PAPERS.map((p) => ({
        "@type": "ScholarlyArticle",
        "@id": `https://doi.org/${p.doi}`,
        name: p.title,
        headline: p.title,
        identifier: [
            { "@type": "PropertyValue", propertyID: "DOI", value: p.doi },
        ],
        sameAs: p.url,
        url: p.url,
        datePublished: "2026",
        inLanguage: "en",
        author: {
            "@type": "Person",
            name: METHODOLOGY_AUTHOR.name,
            identifier: METHODOLOGY_AUTHOR.orcidUrl,
            sameAs: METHODOLOGY_AUTHOR.orcidUrl,
            affiliation: METHODOLOGY_AUTHOR.affiliation,
        },
        publisher: {
            "@type": "Organization",
            name: "Zenodo",
            url: "https://zenodo.org",
        },
        isPartOf: {
            "@type": "PublicationIssue",
            name: "TRT Plus Research",
            url: "https://zenodo.org/communities/trtplus-research",
        },
    })),
};

function useEngineMeta() {
    useEffect(() => {
        const title = "The Engine · Schematic + Formulas · TRT Plus";
        const description =
            "The TRT Plus engine, under the hood. Interactive schematic of 16 biomarkers, 8 clinical relationships, 2 hormone ratios — and every formula that powers the score, from PK modeling to the cardio-metabolic cascade.";
        const ogDescription =
            "Interactive schematic of 16 biomarkers, 8 clinical relationships, and every formula behind the TRT Plus score — PK modeling to the cardio-metabolic cascade.";
        const url = "https://trtplus.app/engine";

        const prev = {};
        const set = (selector, attr, value) => {
            const el = document.querySelector(selector);
            if (!el) return;
            prev[selector] = el.getAttribute(attr);
            el.setAttribute(attr, value);
        };
        const prevTitle = document.title;
        document.title = title;

        set('meta[name="description"]', "content", description);
        set('link[rel="canonical"]', "href", url);
        set('meta[property="og:title"]', "content", title);
        set('meta[property="og:description"]', "content", ogDescription);
        set('meta[property="og:url"]', "content", url);
        set('meta[name="twitter:title"]', "content", title);
        set('meta[name="twitter:description"]', "content", ogDescription);

        if (!window.location.hash) {
            window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
        }

        return () => {
            document.title = prevTitle;
            const restore = (selector, attr) => {
                const el = document.querySelector(selector);
                if (el && prev[selector] != null) el.setAttribute(attr, prev[selector]);
            };
            restore('meta[name="description"]', "content");
            restore('link[rel="canonical"]', "href");
            restore('meta[property="og:title"]', "content");
            restore('meta[property="og:description"]', "content");
            restore('meta[property="og:url"]', "content");
            restore('meta[name="twitter:title"]', "content");
            restore('meta[name="twitter:description"]', "content");
        };
    }, []);
}

export default function Engine() {
    useEngineMeta();
    useBreadcrumbJsonLd(ENGINE_CRUMBS);
    useJsonLd("engine-techarticle", ENGINE_ARTICLE);
    useJsonLd("engine-methodology-papers", METHODOLOGY_ARTICLE_JSONLD);
    const { hash } = useLocation();
    useEffect(() => {
        if (!hash) return;
        const id = hash.slice(1);
        const tryScroll = (attempts = 0) => {
            const el = document.getElementById(id);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
            } else if (attempts < 10) {
                setTimeout(() => tryScroll(attempts + 1), 80);
            }
        };
        tryScroll();
    }, [hash]);
    return (
        <main
            data-testid="engine-page"
            className="relative bg-black text-[#f4f5f7] font-body min-h-screen"
        >
            <Navbar />

            {/* Slim breadcrumb bar — minimal back-to-home affordance */}
            <div
                id="top"
                data-testid="engine-page-crumb"
                className="pt-24 lg:pt-28"
            >
                <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-6 pb-2">
                    <Link
                        to="/"
                        data-testid="engine-back-link"
                        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-mono text-white/55 hover:text-white transition-colors"
                    >
                        <ArrowLeft size={14} /> Back to home
                    </Link>
                </div>
            </div>

            {/* Engine Schematic */}
            <section
                id="schematic"
                data-testid="engine-page-schematic"
                className="relative pt-10 pb-20 lg:pb-28 px-6 sm:px-8 lg:px-12 bg-black"
            >
                <div className="max-w-7xl mx-auto">
                    <EngineSchematic showHeader={true} />
                </div>
            </section>

            {/* Under The Hood — formula library */}
            <section id="formulas" data-testid="engine-page-formulas">
                <UnderTheHood />
            </section>

            {/* Related reading — curated blog cards that explain the
                engine from different angles. Order is editorially set,
                not auto-sorted, so flagship pillars sit first. */}
            <section
                id="related-reading"
                data-testid="engine-related-reading"
                className="relative py-20 lg:py-24 px-6 sm:px-8 lg:px-12 bg-black border-t border-white/5"
            >
                <div className="max-w-7xl mx-auto">
                    <div className="mb-10 lg:mb-14">
                        <div className="eyebrow mb-5">
                            <span className="dot" /> Related reading
                        </div>
                        <h2 className="font-display font-semibold text-white text-2xl lg:text-3xl leading-tight max-w-2xl">
                            Deeper reads on the engine, scored
                            end-to-end.
                        </h2>
                    </div>

                    <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="engine-related-list">
                        {["the-overall-score", "the-growth-axis", "built-here-first"]
                            .map((slug) => BLOG_META.find((p) => p.slug === slug))
                            .filter(Boolean)
                            .map((post) => (
                                <li key={post.slug}>
                                    <Link
                                        to={`/blog/${post.slug}`}
                                        data-testid={`engine-related-${post.slug}`}
                                        className="tile group block p-6 h-full hover:border-white/20 transition-colors relative"
                                    >
                                        <ArrowLeft
                                            size={14}
                                            className="absolute top-4 right-4 text-white/30 rotate-[135deg] group-hover:text-[#49bef5] transition-colors"
                                            aria-hidden="true"
                                        />
                                        <div className="text-[10.5px] uppercase tracking-[0.18em] font-mono text-[#49bef5] mb-3">
                                            {post.category}
                                        </div>
                                        <h3 className="font-display font-semibold text-white text-[19px] lg:text-[20px] leading-tight mb-3">
                                            {post.title}
                                        </h3>
                                        <p className="text-white/55 text-[13.5px] leading-relaxed mb-4 line-clamp-3">
                                            {post.description}
                                        </p>
                                        <div className="text-[10.5px] font-mono uppercase tracking-[0.18em] text-white/40">
                                            {post.readingTime}
                                        </div>
                                    </Link>
                                </li>
                            ))}
                    </ul>
                </div>
            </section>

            {/* Page footer — terminal-style closure */}
            <section
                data-testid="engine-page-footer-cta"
                className="relative py-20 lg:py-28 px-6 sm:px-8 lg:px-12 bg-black border-t border-white/5"
            >
                <div className="max-w-3xl mx-auto text-center">
                    <p
                        className="font-mono text-[11px] lg:text-[12px] uppercase tracking-[0.22em] text-white/45 mb-6"
                        data-testid="engine-page-readout-complete"
                    >
                        <span className="text-[#00E6A0] mr-2">{">"}</span>
                        Engine readout complete
                    </p>
                    <h2 className="font-display font-semibold text-white text-2xl lg:text-3xl leading-snug">
                        The engine lives inside TRT Plus.
                    </h2>
                    <div className="mt-10 flex justify-center">
                        <StoreButtons testIdPrefix="engine-footer" />
                    </div>
                    <div className="mt-10">
                        <Link
                            to="/"
                            data-testid="engine-page-back-home"
                            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-mono text-white/55 hover:text-white transition-colors"
                        >
                            <ArrowLeft size={14} /> Back to home
                        </Link>
                    </div>
                </div>
            </section>

            <Footer />
        </main>
    );
}
